import { Trans } from '~/common/context';
import { Alert, Text } from '@wyz/ui';

import { IOrder } from '@wyz/types';
import { ORDER_TYPE_CODE } from '@wyz/utils';
import { SuggestedTyreRow } from '~/purchase/components';
import { RedistributionOrderHelpers } from '~/purchase/helpers';
import {
  IAcceptOrderValidationErrorResponse,
  ISuggestedOffer,
} from '~/purchase/types';
import { isNil, sumBy } from 'lodash-es';

type Props = {
  offers: ISuggestedOffer[];
  changes: IAcceptOrderValidationErrorResponse[];
  order: IOrder;
  isFullyCoveredByLocalStock: boolean;
  isLoading: boolean;
  orderItemIndex: number;
};

function isOriginalStillAvailable(
  order: Props['order'],
  suggestedOffers: Props['offers'],
) {
  const hasOriginallyChosenAlternative = order.alternative;
  // for alternative we don't care about the supplier, it just has be another available alternative offer
  if (hasOriginallyChosenAlternative) {
    return suggestedOffers.some(
      (suggestedOffer) =>
        suggestedOffer.offer.available && suggestedOffer.offer.alternativeOffer,
    );
  }

  // for other offers (manuf/intra), it has to be the same supplier
  return suggestedOffers.find(
    (suggestedOffer) =>
      suggestedOffer.offer.available &&
      suggestedOffer.offer.supplier.id === order.supplierId,
  );
}

function SuggestedTyreTable({
  offers,
  changes,
  order,
  isFullyCoveredByLocalStock,
  isLoading,
  orderItemIndex,
}: Props) {
  // supplier has changed between N2 order and new offers
  const originalOfferNotAvailable = !isOriginalStillAvailable(order, offers);
  const mappedChanges = RedistributionOrderHelpers.getMappedChanges(
    offers,
    changes,
    order,
    orderItemIndex,
  );

  const isEmptyStock =
    changes.length > 0 &&
    changes.every((item) => item.actual.availableQuantity === 0);

  const totalRequestedQuantity = sumBy(
    order.tyreLines,
    (item) => item.quantity,
  );
  const totalAvailableQuantity = sumBy(
    offers,
    (item) => item.offer.availableQuantity,
  );

  const isPartiallyAvailable =
    totalAvailableQuantity > 0 &&
    totalAvailableQuantity < totalRequestedQuantity;

  const isDisplayedBf =
    !isNil(offers.at(0)?.tyre.priceFrame.price) ||
    (originalOfferNotAvailable &&
      !isFullyCoveredByLocalStock &&
      !isNil(order.tyreLines.at(orderItemIndex)?.resolvedBf));

  return (
    <>
      {(offers.length === 0 || isEmptyStock) && (
        <Alert variant='danger'>
          <Trans
            i18nKey={
              order.parentStockOrder
                ? 'app.purchase.redistribution.pending_order.accept.parent_out_of_stock'
                : 'app.purchase.redistribution.pending_order.accept.out_of_stock'
            }
          />
        </Alert>
      )}
      {offers.length > 0 && !isEmptyStock && (
        <table className='w-100 mt-2'>
          <thead className='bg-white shadow-none'>
            <tr>
              <th className='text-left bg-gray'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.supplier' />
                </Text>
              </th>
              {isDisplayedBf && (
                <th className='text-center bg-white'>
                  <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                    <Trans i18nKey='app.common.price.bf' />
                  </Text>
                </th>
              )}
              <th className='text-center bg-white'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.orders.delivery_date' />
                </Text>
              </th>
              <th className='text-center bg-white'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.status.availibity' />
                </Text>
              </th>
              <th className='text-center bg-white'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.common.quantity' />
                </Text>
              </th>
              <th className='text-center bg-white'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.buying_pricing_unit' />
                </Text>
              </th>
              <th className='text-center bg-white'>
                <Text heading='xs' weight='semi-bold' color='secondary-gray'>
                  <Trans i18nKey='app.purchase.redistribution.pending_order.accept.selling_pricing_unit' />
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {originalOfferNotAvailable && !isFullyCoveredByLocalStock && (
              <SuggestedTyreRow
                key={`${order.orderReference}`}
                index={-1}
                supplier={{
                  id: order.supplierId,
                  name: order.supplierName,
                }}
                order={order}
                tyre={{
                  ean: order.tyreLines.at(orderItemIndex)?.ean ?? '',
                  priceFrame: {
                    price:
                      order.tyreLines.at(orderItemIndex)?.resolvedBf ?? null,
                  },
                }}
                offer={{
                  deliveryDate: order.dateDelivered,
                  availableQuantity: 0,
                  isBackOrder: order.type === ORDER_TYPE_CODE.BO,
                  redistributionSellingPrice:
                    order.tyreLines.at(orderItemIndex)
                      ?.redistributionSellingPrice,
                  redistributionBuyingPrice:
                    order.tyreLines.at(orderItemIndex)
                      ?.redistributionBuyingPrice,
                }}
              />
            )}
            {offers.map((suggestedOffer, offerIndex) => {
              return (
                <SuggestedTyreRow
                  key={`${offerIndex}-${suggestedOffer.offer.supplier.id}`}
                  supplier={suggestedOffer.offer.supplier}
                  index={offerIndex}
                  isProcessing={isLoading}
                  offer={suggestedOffer.offer}
                  tyre={{
                    ean: suggestedOffer.tyre.ean,
                    priceFrame: {
                      price: suggestedOffer.tyre.priceFrame.price,
                    },
                  }}
                  change={mappedChanges.get(suggestedOffer.offerId)}
                  order={order}
                  oldValues={{
                    date: order.dateDelivered,
                    quantity: order.tyreLines.at(orderItemIndex)?.quantity,
                    sellingPrice:
                      order.tyreLines.at(orderItemIndex)
                        ?.redistributionSellingPrice,
                  }}
                  isPartiallyAvailable={isPartiallyAvailable}
                />
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

export default SuggestedTyreTable;
